import { coerceNumberProperty } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import {
    AfterContentChecked,
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges
} from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { fromEvent, merge, Subscription } from 'rxjs';
import { ScrollService } from '@df/util/service/scroll.service';
import { Events } from '@df/core/service/event';
import { App } from '@df/core/app';
import { BodyClassService } from '@df/ui/body-class.service';
import { Meta } from '@df/core/meta/meta';
import { TimeoutService } from '@df/ng/timeout.service';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { Client } from '@df/core/service/client';

export class StickyChangeEvent {
    constructor(
        public isSticky: boolean,
        public stickyDirective: StickyDirective
    ) {}
}

/**
 * @param stickyOffsetSectionId {string}
 *      Offset element ID (if not set sticky mode is enabled when elem gets of the screen;
 *      use this attribute when you have fixed position header).
 * @param stickyBottomSectionId {string}
 *      Offset element ID (if not set sticky mode is enabled when elem gets of the screen;
 *      use this attribute when you have fixed position header).
 * @param stickyTopSectionId {string}
 *      Element ID - height of this element set sticky top position.
 * @param stickyShift {number}
 *      This value (in px) will be added to top.
 * @param stickyTransition {number}
 *      Transition time.
 * @param stickyToBottom {boolean}
 *      If `true` will invert logic to make sticky at the bottom of page.
 * @param stickyByBottom {boolean}
 *      If `true` will start scrolling when bottom part gets into viewport.
 * @param stickyTillFits {boolean}
 *      If `true` then sticky will be disabled if sticky element height + offset is bigger then body height.
 * @param stickyDetectDirection {boolean}
 *      If `true` then add class for scroll down and up.
 * @param stickyActivationOffset {number}
 *      Ammends sitcky mode switching point, in px.
 * @param stickyOff {boolean}
 *      If `true` will disable sticky feature.
 */
@Directive({
    selector: 'sticky,[sticky]',
    standalone: true
})
/**
 * @depreciated Use css position:sticky instead
 */
export class StickyDirective implements OnInit, OnChanges, OnDestroy, AfterViewInit, AfterContentChecked {
    protected subscriptions: Subscription = new Subscription();

    protected wrap?: HTMLElement;

    // flag that is true when parent element got height assigned already
    protected wrapHeightSet?: number;

    // element rectangle
    protected elementRect?: ClientRect;

    // scroll direction
    protected lastScrollTop = 0;

    // offset section
    protected offsetSection?: HTMLElement;

    // bottom section
    protected bottomSection?: HTMLElement;

    // top section
    protected topSection?: HTMLElement;

    // is sticky
    protected isSticky: boolean = false;
    private _lastIsSticky: boolean = false;

    // top shift
    protected shift = 0;

    @Input()
    stickyOff?: boolean;

    @Input()
    stickyDetectDirection?: boolean;

    @Input()
    stickyOffsetSectionId?: string;

    @Input()
    stickyBottomSectionId?: string;

    @Input()
    stickyTop?: number;

    @Input()
    stickyTopSectionId?: string;

    @Input()
    stickyToBottom?: boolean;

    @Input()
    stickyByBottom?: boolean;

    @Input()
    stickyShift?: number;

    @Input()
    stickyActivationOffset?: number;

    @Input()
    stickyStaticHeight?: boolean;

    @Input()
    stickyTillFits?: boolean;

    @Input()
    stickyTillTaller?: boolean;

    @Input()
    stickyTransition?: number;

    @Input()
    stickyScroll?: boolean;

    @Input()
    stickyBodyClass?: string;

    scrollDirectionApply: () => void;

    /** event emitted when sticky state change */
    @Output() stickyChange = new EventEmitter<StickyChangeEvent>();

    update = (): void => this._update();

    constructor(
        @Inject(DOCUMENT) private document: Document,
        protected elementRef: ElementRef,
        private renderer2: Renderer2,
        private client: Client,
        private scrollService: ScrollService,
        private events: Events,
        private app: App,
        private bodyClassService: BodyClassService,
        private meta: Meta,
        timeoutService: TimeoutService
    ) {
        this.scrollDirectionApply = timeoutService.throttle(() => {
            if (this.isSticky) {
                const scrollTop: number = this.scrollService.getScrollTop();

                if (scrollTop > this.lastScrollTop) {
                    this.toggleStickyUp();
                    this.toggleStickyDown(true);
                } else {
                    this.toggleStickyDown();

                    if (this.lastScrollTop > scrollTop) {
                        this.toggleStickyUp(true);
                    }
                }

                this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
            } else {
                this.lastScrollTop = 0;
                this.toggleStickyUp();
                this.toggleStickyDown();
            }
        }, 50);
    }

    ngOnInit(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        const element = this.client.scrollElementForEvents;
        this.subscriptions.add(
            merge(
                fromEvent(element, 'scroll'),
                this.client.obs.resize,
                fromEvent(element, 'touchmove'),
                fromEvent(element, 'cssUpdate'),
                this.events.on('cms.editor.toggle'),
                // use this event if You need to update sticky manually,
                // for example if You have two sticky elements and top one changes its height.
                this.events.on('sticky.update')
            ).subscribe(() => this.update())
        );

        // reset class
        this.subscriptions.add(
            this.meta.routerEvents.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.reset();
                }
                if (event instanceof NavigationEnd) {
                    this.toggleBodyClass();
                    this.toggleStickyDown();
                    this.toggleStickyUp();
                }
            })
        );

        this.subscriptions.add(resizeObservable(this.element, () => !this.stickyStaticHeight && this.setStickyContainer()).subscribe());
        this.subscriptions.add(this.events.on('sticky.update-height').subscribe(() => this.setStickyContainer()));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        if (changes['stickyOff'] || changes['stickyStaticHeight']) {
            this.update();
        }
    }

    ngAfterViewInit(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.wrap = this.renderer2.createElement('div');
        this.wrapElement(this.element, this.wrap!);
    }

    ngAfterContentChecked(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.update();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.reset();

        if (this.wrap?.parentNode) {
            this.renderer2.removeChild(this.wrap.parentNode, this.wrap);
        }
    }

    get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    protected _update(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.isSticky = false;
        const styles: any = [];

        if (!this.stickyOff && !this.app.client.isCms && this.wrap) {
            const wrapRect = this.wrap.getBoundingClientRect();
            const elementRect = this.element.getBoundingClientRect();
            const scrollTop = this.scrollingElement.scrollTop;
            const byBottom = !!this.stickyByBottom;
            const viewportHeight = this.client.height;
            let maxTop = this.client.height - elementRect.height;
            let minBottom = 0;

            if (this.stickyOffsetSectionId) {
                this.offsetSection = document.getElementById(this.stickyOffsetSectionId) as HTMLElement | undefined;
            }

            if (this.stickyBottomSectionId) {
                this.bottomSection = document.getElementById(this.stickyBottomSectionId) as HTMLElement | undefined;
            }

            let useStickyTop = false;
            if (this.stickyTopSectionId) {
                this.topSection = document.getElementById(this.stickyTopSectionId) as HTMLElement | undefined;
            }

            if (!this.wrapHeightSet || !this.stickyStaticHeight) {
                this.setStickyContainer();
            }

            let top = 0;
            let left = 0;
            let width = 0;

            if (this.stickyTop !== undefined) {
                useStickyTop = true;
            }
            let stickyTop = coerceNumberProperty(this.stickyTop, 0);

            if (this.topSection) {
                useStickyTop = true;
                stickyTop = stickyTop + this.topSection.offsetHeight;
            }

            const shift = coerceNumberProperty(this.stickyShift, 0);
            const stickyActivationOffset = coerceNumberProperty(this.stickyActivationOffset, 0);
            const toBottom = this.stickyToBottom || this.stickyByBottom;
            const tillFits = this.stickyTillFits;
            const tillTaller = this.stickyTillTaller;

            if ((!tillFits || elementRect.height + stickyTop < viewportHeight) && (!tillTaller || wrapRect.height > viewportHeight)) {
                let offsetSectionHeight = 0;

                if (this.offsetSection) {
                    const offsetSectionRect = this.offsetSection.getBoundingClientRect();

                    offsetSectionHeight = offsetSectionRect.height + offsetSectionRect.top;
                }

                const offset = offsetSectionHeight;

                if (this.bottomSection) {
                    const bottomSectionRect = this.bottomSection.getBoundingClientRect();
                    maxTop -= Math.max(0, viewportHeight - bottomSectionRect.top);
                    minBottom = viewportHeight - bottomSectionRect.top;
                }
                top = useStickyTop ? stickyTop : Math.round((offsetSectionHeight || wrapRect.top + scrollTop) + shift);
                left = Math.round(wrapRect.left);
                width = Math.round(wrapRect.width);

                if (byBottom) {
                    this.isSticky = wrapRect.bottom - this.client.height < 0;
                } else {
                    this.isSticky = !!(wrapRect.top + stickyActivationOffset < offset || (this.offsetSection && offset < 0));
                }
            }

            if (this.isSticky) {
                styles.push('position: fixed');

                if (this.stickyTransition) {
                    const stickyTransition = coerceNumberProperty(this.stickyTransition, 0);
                    styles.push(`transition: ${stickyTransition}s ease`);
                }

                if (toBottom) {
                    styles.push(`bottom: ${Math.max(0, minBottom)}px`);
                } else {
                    styles.push(`top: ${Math.min(top, maxTop)}px`);
                }

                styles.push(`left: ${left}px`);
                styles.push(`width: ${width}px`);
            }

            // in this mode we will set max so that container has own scroll
            if (this.stickyScroll) {
                let bottomSectionVisiblePart = viewportHeight;

                if (this.bottomSection) {
                    const bottomSectionRect = this.bottomSection.getBoundingClientRect();

                    bottomSectionVisiblePart -= bottomSectionRect.top;
                } else {
                    bottomSectionVisiblePart = 0;
                }

                let maxHeight = Math.round(
                    viewportHeight - (this.isSticky ? top : elementRect.top) - Math.max(0, bottomSectionVisiblePart)
                );

                if (maxHeight < 0) {
                    maxHeight = 0;
                }

                styles.push(`max-height: ${maxHeight}px`);
                styles.push('overflow-y: auto');
            }
        } else if (this.wrap) {
            this.unsetStickyContainer();
        }

        const style = styles.join(';');

        this.renderer2.setAttribute(this.element, 'style', style);

        // apply sticky class
        this.stickyClassApply();

        // apply directional classes
        if (this.stickyDetectDirection) {
            this.scrollDirectionApply();
        }
    }

    setStickyContainer(): void {
        if (this.wrap) {
            const elementRect = this.element.getBoundingClientRect();
            this.renderer2.setAttribute(this.wrap, 'style', `display: block; height: ${elementRect.height}px`);
            this.renderer2.addClass(this.wrap, 'sticky-container');

            this.wrapHeightSet = elementRect.height;
        }
    }

    unsetStickyContainer(): void {
        this.renderer2.removeAttribute(this.wrap, 'style');
        this.renderer2.removeClass(this.wrap, 'sticky-container');
        this.wrapHeightSet = undefined;
    }

    stickyClassApply(): void {
        this.isSticky ? this.renderer2.addClass(this.element, 'is-sticky') : this.renderer2.removeClass(this.element, 'is-sticky');

        this.toggleBodyClass(!!this.isSticky);

        if (this._lastIsSticky !== this.isSticky) {
            this._lastIsSticky = this.isSticky;

            this.stickyChange.emit(new StickyChangeEvent(this.isSticky, this));
        }

        if (this._lastIsSticky !== this.isSticky) {
            this._lastIsSticky = this.isSticky;

            this.stickyChange.emit(new StickyChangeEvent(this.isSticky, this));
        }
    }

    toggleBodyClass(to = false): void {
        if (this.stickyBodyClass) {
            this.bodyClassService.set(this.stickyBodyClass, this, to);
        }
    }

    isStickyUp() {
        this.element.classList.add('is-sticky-up');
    }

    toggleStickyUp(to = false): void {
        const className = 'is-sticky-up';
        to
            ? this.renderer2.addClass(this.elementRef.nativeElement, className)
            : this.renderer2.removeClass(this.elementRef.nativeElement, className);

        if (this.stickyBodyClass) {
            this.bodyClassService.set(`${this.stickyBodyClass}-${className}`, this, to);
        }
    }

    toggleStickyDown(to = false): void {
        const className = 'is-sticky-down';
        to
            ? this.renderer2.addClass(this.elementRef.nativeElement, className)
            : this.renderer2.removeClass(this.elementRef.nativeElement, className);

        if (this.stickyBodyClass) {
            this.bodyClassService.set(`${this.stickyBodyClass}-${className}`, this, to);
        }
    }

    reset(): void {
        this.toggleBodyClass();
        this.toggleStickyDown();
        this.toggleStickyUp();
    }

    protected wrapElement(element: HTMLElement, wrapper: HTMLElement): void {
        element.parentNode!.insertBefore(wrapper, element);
        wrapper.appendChild(element);
    }

    private get scrollingElement(): HTMLElement {
        return (this.client.getRootElement() || this.document.scrollingElement) as HTMLElement;
    }
}
